import * as types from '@/store/mutation-types'
import Vue from 'vue'
import {
  loadExploitation,
  loadAnneesSeches,
  loadSimuClim,
  saveExploitation,
  createIlot,
  updateIlot,
  removeIlot,
  createCulture,
  updateCulture,
  removeCulture,
  duplicateCulture
} from '@/api/client-api'

export default {
  state: {
    information: null,
    ilots: [],
    cultures: [],
    solIlots: {},
    solCultures: {},
    stadeCultures: {},
    currentIlot: null,
    currentCulture: null
  },
  mutations: {
    [types.SET_INFORMATION] (state, information) {
      state.information = information
    },

    // -------------------------------------------------------------
    [types.SET_CURRENT_ILOT] (state, id) {
      state.currentIlot = id
    },
    [types.SET_ILOTS] (state, ilots) {
      state.ilots = ilots
    },
    [types.ADD_ILOT] (state, ilot) {
      state.ilots.push(ilot)
      state.ilots.sort((a, b) => a.nom.localeCompare(b.nom))
    },
    [types.SET_ILOT] (state, ilot) {
      let idx = state.ilots.findIndex(i => i.id_ilot === ilot.id_ilot)
      Vue.set(state.ilots, idx, ilot)
    },
    [types.REMOVE_ILOT] (state, id) {
      state.ilots.splice(state.ilots.findIndex(e => e.id_ilot === id), 1)
    },
    // -------------------------------------------------------------
    [types.SET_SOL_ILOTS] (state, solIlots) {
      state.solIlots = solIlots
    },
    [types.SET_SOL_ILOT] (state, data) {
      state.solIlots[data.id_ilot] = data.sols
    },

    // -------------------------------------------------------------
    [types.SET_SOL_CULTURES] (state, solCultures) {
      state.solCultures = solCultures
    },
    [types.SET_SOL_CULTURE] (state, data) {
      state.solCultures[data.id_culture] = data.sols
    },

    // -------------------------------------------------------------
    [types.SET_STADE_CULTURES] (state, stadeCultures) {
      state.stadeCultures = stadeCultures
    },
    [types.SET_STADE_CULTURE] (state, data) {
      state.stadeCultures[data.id_culture] = data.stades
    },

    // -------------------------------------------------------------
    [types.SET_CURRENT_CULTURE] (state, id) {
      state.currentCulture = id
    },
    [types.SET_CULTURES] (state, cultures) {
      state.cultures = cultures
    },
    [types.ADD_CULTURE] (state, culture) {
      state.cultures.push(culture)
    },
    [types.SET_CULTURE] (state, culture) {
      let idx = state.cultures.findIndex(i => i.id_culture === culture.id_culture)
      Vue.set(state.cultures, idx, culture)
    },
    [types.REMOVE_CULTURE] (state, id) {
      state.cultures.splice(state.cultures.findIndex(e => e.id_culture === id), 1)
    }
  },
  actions: {
    loadExploitation: ({ commit, state }, id) => {
      if (state.exploitation && state.exploitation.id_exploitation === id) {
        return Promise.resolve()
      }
      commit(types.ADD_LOADING_MESSAGES, "Chargement de l'exploitation")
      commit(types.SET_INFORMATION, null)

      return loadExploitation(id)
        .then(res => {
          commit(types.SET_INFORMATION, res.information)
          commit(types.SET_ILOTS, res.ilots)
          commit(types.SET_SOL_ILOTS, res.ilots_sols)
          commit(types.SET_CULTURES, res.cultures)
          commit(types.SET_SOL_CULTURES, res.cultures_sols)
          commit(types.SET_STADE_CULTURES, res.cultures_stades)
          commit(types.SET_SIMULATIONS, res.simulations)
        })
        .finally(() => commit(types.DEL_LOADING_MESSAGES))
    },
    loadAnneesSeches: ({ commit }, id) => {
      return loadAnneesSeches(id)
        .then(res => {
          commit(types.SET_ANNEES_SECHES, res)
        })
    },
    loadSimuClim: ({ commit }, id) => {
      return loadSimuClim(id)
        .then(res => {
          commit(types.SET_SIMU_CLIM, res)
        })
    },
    saveExploitation: ({ commit }, data) => {
      commit(types.ADD_LOADING_MESSAGES, 'Sauvegarde des informations')
      return saveExploitation(data.id_exploitation, data)
        .then(information => commit(types.SET_INFORMATION, information))
        .finally(() => commit(types.DEL_LOADING_MESSAGES))
    },
    // -------------------------------------------------------------
    createIlot: ({ commit }, { idExploitation, nom }) => {
      commit(types.ADD_LOADING_MESSAGES, "Création d'un ilot")
      return createIlot(idExploitation, nom)
        .then(ilot => {
          commit(types.ADD_ILOT, ilot)
          return ilot.id_ilot
        })
        .finally(() => commit(types.DEL_LOADING_MESSAGES))
    },
    removeIlot: ({ commit }, { idExploitation, idIlot }) => {
      commit(types.ADD_LOADING_MESSAGES, "Suppression d'un ilot")
      return removeIlot(idExploitation, idIlot)
        .then(data => {
          commit(types.REMOVE_ILOT, idIlot)
          return data
        })
        .finally(() => commit(types.DEL_LOADING_MESSAGES))
    },
    saveIlot: ({ commit, getters }, data) => {
      commit(types.ADD_LOADING_MESSAGES, "Sauvegarde d'un ilot")
      return updateIlot(getters.exploitationId, data.id_ilot, data)
        .then((updatedData) => {
          commit(types.SET_SOL_ILOT, {
            id_ilot: data.id_ilot,
            sols: updatedData.sols
          })
          commit(types.SET_SOL_CULTURES, updatedData.cultures_sols)
          commit(types.SET_ILOT, updatedData.ilot)
        })
        .finally(() => commit(types.DEL_LOADING_MESSAGES))
    },
    // -------------------------------------------------------------
    createCulture: ({ commit }, { idExploitation, idRefCulture }) => {
      commit(types.ADD_LOADING_MESSAGES, "Création d'une culture")
      return createCulture(idExploitation, idRefCulture)
        .then(data => {
          commit(types.ADD_CULTURE, data.culture)
          commit(types.SET_SOL_CULTURE, {
            id_culture: data.culture.id_culture,
            sols: data.sols
          })
          return data.culture.id_culture
        })
        .finally(() => commit(types.DEL_LOADING_MESSAGES))
    },
    removeCulture: ({ commit }, { idExploitation, idCulture }) => {
      commit(types.ADD_LOADING_MESSAGES, "Suppression d'une culture")
      return removeCulture(idExploitation, idCulture)
        .then(data => {
          commit(types.REMOVE_CULTURE, idCulture)
          return data
        })
        .finally(() => commit(types.DEL_LOADING_MESSAGES))
    },
    duplicateCulture: ({ commit }, { idExploitation, idCulture }) => {
      commit(types.ADD_LOADING_MESSAGES, "Duplication d'une culture")
      return duplicateCulture(idExploitation, idCulture)
        .then(updateData => {
          commit(types.ADD_CULTURE, updateData.culture)
          commit(types.SET_SOL_CULTURE, {
            id_culture: updateData.culture.id_culture,
            sols: updateData.sols
          })
          commit(types.SET_STADE_CULTURE, {
            id_culture: updateData.culture.id_culture,
            stades: updateData.stades
          })
          return updateData.culture.id_culture
        })
        .finally(() => commit(types.DEL_LOADING_MESSAGES))
    },
    saveCulture: ({ commit, getters }, data) => {
      commit(types.ADD_LOADING_MESSAGES, "Sauvegarde d'une culture")
      return updateCulture(getters.exploitationId, data.id_culture, data)
        .then((updateData) => {
          commit(types.SET_SOL_CULTURE, {
            id_culture: data.id_culture,
            sols: updateData.sols
          })
          commit(types.SET_STADE_CULTURE, {
            id_culture: data.id_culture,
            stades: updateData.stades
          })
          commit(types.SET_CULTURE, updateData.culture)
        })
        .finally(() => commit(types.DEL_LOADING_MESSAGES))
    }
  },
  getters: {
    exploitationId (state) {
      return state.information && state.information.id_exploitation
    },
    nomExploitation (state) {
      return state.information && state.information.nom
    },
    nomIlot (state, getters) {
      let currentIlot = getters.getIlotById(state.currentIlot)
      return currentIlot ? currentIlot.nom : ''
    },
    nomIlotById: (state, getters) => (id) => {
      let currentIlot = getters.getIlotById(id)
      return currentIlot ? currentIlot.nom : ''
    },
    getCultureById: (state) => (id) => {
      return state.cultures.find(culture => culture.id_culture === id)
    },
    nomCulture (state, getters) {
      let currentCulture = getters.getCultureById(state.currentCulture)
      return currentCulture ? currentCulture.nom : ''
    },
    getIlotById: (state) => (id) => {
      return state.ilots.find(ilot => ilot.id_ilot === id)
    },
    getSolIlotById: (state) => (id) => {
      return (state.solIlots[id] || [])
        .map(s => ({ ...s }))
        .sort((s1, s2) => s1.nom.localeCompare(s2.nom))
    },
    getSolCultureById: (state) => (id) => {
      return (state.solCultures[id] || [])
        .map(s => ({ ...s }))
        .sort((s1, s2) => s1.nom.localeCompare(s2.nom))
    },
    getStadeCultureById: (state) => (id) => {
      return state.stadeCultures[id] || []
    },
    getCultures: (state) => {
      return (state.cultures || [])
        .map(s => ({ ...s }))
        .sort((s1, s2) => s1.nom.localeCompare(s2.nom) || (!!s1.complements && s1.complement.localeCompare(s2.complement)) || s1.id_culture.localeCompare(s2.id_culture))
    },
    getIlots: (state) => {
      return (state.ilots || [])
        .map(s => ({ ...s }))
        .sort((s1, s2) => s1.nom.localeCompare(s2.nom))
    },
    getTypeSol (state, getters, rootState) {
      if (!state.information) {
        return []
      }
      return rootState.referentiel.typeSol
        .filter(s => s.id_ref_region === state.information.commune_reg)
        .sort((a, b) => a.nom.localeCompare(b.nom))
    },
    getTypeSolExploitation (state, getters) {
      let keys = Object.values(state.solIlots)
        .reduce((ids, ilot) => ids.concat(...ilot.map(sol => sol.id_ref_type_sol)), [])
      return getters['referentiel/getTypeSolByIds'](keys)
    }
  }
}
